// The sign-in and quote-lookup panel on the first screen: the pure reading
// of what the visitor typed, of the server's answers, and of the copy each
// outcome shows. The requests themselves live in signInClient.

import { z } from "zod";

import {
  parseGrantLookup,
  type GrantLookup,
  type GrantMethod,
} from "pages/Login/login-grant-flow";

import type { SignInResult } from "./events";
import { isValidEmail, isValidPhone } from "./validation";

export type SignInMode = "sign_in" | "look_up";

export const PANEL_TITLE: Record<SignInMode, string> = {
  sign_in: "Sign in",
  look_up: "Look up your quote",
};

export const IDENTIFIER_LABEL = "Email or mobile phone";

/** Where the panel's password sign-in link leads, back to this flow. */
export const PASSWORD_SIGN_IN_PATH = "/login?redirect=/start";

/** The password sign-in that comes back to `redirect` afterwards. */
export function passwordSignInPath(redirect: string): string {
  return `/login?${new URLSearchParams({ redirect }).toString()}`;
}

export const TRY_ANOTHER_CHANNEL_LABEL = "Try another email or phone";

export const NOT_FOUND_COPY = "We couldn’t find any quotes for that.";

/** What a visitor is told when their proof reached an account that signs in
 * another way. */
export function accountChannelCopy(): string {
  return "That isn’t how your account signs in. Use the phone or email you sign in with, or give us a call.";
}

/** What the typed identifier is, or null when it is neither an email nor a
 * US phone number. */
export function identifierKindOf(raw: string): "email" | "phone" | null {
  const value = raw.trim();
  if (value.includes("@")) return isValidEmail(value) ? "email" : null;
  return isValidPhone(value) ? "phone" : null;
}

/** The method a typed email or phone is sent by, or null when it is
 * neither. */
export function identifierMethodOf(raw: string): GrantMethod | null {
  const kind = identifierKindOf(raw);
  if (kind === null) return null;
  return kind === "phone" ? "sms_otp" : "email_link";
}

export function identifierError(raw: string): string | null {
  if (raw.trim() === "") return "Enter your email or mobile phone.";
  if (identifierKindOf(raw) === null) {
    return "That doesn’t look like an email or a US mobile phone. Please check it.";
  }
  return null;
}

const startedSignInSchema = z.object({
  login_grant: z.string().regex(/^[0-9a-f]{64}$/),
  methods: z.array(z.string()),
  phone_display: z.string().nullable().optional(),
  email_display: z.string().nullable().optional(),
});

export type StartedSignIn = { loginGrant: string; lookup: GrantLookup };

/** The sign-in start route's answer, or null when it is not a usable one.
 * The grant signs in to the path it was started for: the start screen
 * unless the request named a quote. */
export function parseStartedSignIn(
  raw: unknown,
  redirectPath = "/start",
): StartedSignIn | null {
  const parsed = startedSignInSchema.safeParse(raw);
  if (!parsed.success) return null;
  const body = parsed.data;
  const lookup = parseGrantLookup({
    purpose: "channel_sign_in",
    methods: body.methods,
    phone_display: body.phone_display,
    email_display: body.email_display,
    redirect_path: redirectPath,
  });
  return lookup ? { loginGrant: body.login_grant, lookup } : null;
}

const SIGNUP_TOKEN = /^[A-Za-z0-9_-]{16,128}$/;

/** The single-use account-creation token a magic link left in router
 * state, or null when the state carries none. */
export function readSignupToken(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const token = (state as Record<string, unknown>).signupToken;
  return typeof token === "string" && SIGNUP_TOKEN.test(token) ? token : null;
}

export type SignInFailure =
  | "invalid_identifier"
  | "rate_limited"
  | "unavailable"
  | "token_invalid"
  | "other";

const FAILURE_COPY: Record<SignInFailure, string> = {
  invalid_identifier:
    "That doesn’t look like an email or a US mobile phone. Please check it.",
  rate_limited:
    "That didn’t go through. Please wait a moment and try again, or give us a call.",
  unavailable:
    "We can’t look that up right now. Please give us a call and we’ll help.",
  token_invalid: "That took too long. Please start again.",
  other: "Something went wrong. Please try again.",
};

/** The line shown for a failed sign-in. */
export function signInFailureCopy(failure: SignInFailure): string {
  return FAILURE_COPY[failure];
}

/** What a refused request to start a sign-in means. */
export function startSignInFailure(
  statusCode: number | undefined,
): SignInFailure {
  if (statusCode === 422) return "invalid_identifier";
  if (statusCode === 429) return "rate_limited";
  if (statusCode === 503) return "unavailable";
  return "other";
}

/** What a refused request to create the account means: a token that is
 * spent, expired or unknown can only be replaced by starting again. */
export function startSignUpFailure(
  statusCode: number | undefined,
): SignInFailure {
  if (statusCode === 429) return "rate_limited";
  if (statusCode === 503) return "unavailable";
  if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
    return "token_invalid";
  }
  return "other";
}

/** The result to report for the answer to starting a sign-in. */
export function startResultOf(
  answer: { kind: "started" } | { kind: "failed"; failure: SignInFailure },
): SignInResult {
  return answer.kind === "started"
    ? { result: "code_sent" }
    : failedResultOf(answer.failure);
}

/** The result to report for a refused request. */
export function failedResultOf(failure: SignInFailure): SignInResult {
  return { result: "failed", reason: failure };
}

/** The result to report once the proof shows nobody is signed in by it: a
 * channel nobody holds, or one on an account that signs in another way. */
export function completionResultOf(
  completion: { kind: "create_account" } | { kind: "use_account_channel" },
): SignInResult {
  return {
    result:
      completion.kind === "create_account" ? "create_account" : "other_channel",
  };
}
